/**
 * TournamentBracket – Turnierbaum mit SVG-Verbindungslinien
 *
 * Props:
 *   matches        – alle KO-Matches (Gruppen bereits herausgefiltert)
 *   teams          – Team-Array für Spieler-Lookup
 *   readOnly       – boolean (keine Klick-Handler)
 *   compact        – boolean (kleinere Karten für Presentation View)
 *   isDarkMode     – boolean
 *   onMatchClick   – fn(match) – Callback beim Klick (Admin)
 */
import React from 'react';

// ── Layout-Konfigurationen ─────────────────────────────────────────────────────
const NORMAL_LAYOUT  = { CW: 255, GV: 32, GH: 72, OX: 16, OY: 40, CH_RO: 82,  CH_ADMIN: 112 };
const COMPACT_LAYOUT = { CW: 165, GV: 16, GH: 38, OX: 12, OY: 26, CH_RO: 62,  CH_ADMIN: 72 };

const GOLD  = '#e8b73a';
const GREEN = '#22c55e';

// ── Runden-Konfiguration ───────────────────────────────────────────────────────
const MAIN_ORDER = ['Runde 1', 'Achtelfinale', 'Viertelfinale', 'Halbfinale', 'Finale'];
const PLACE3     = 'Spiel um Platz 3';

const PLACEMENT_CONFIGS = [
  { key: '5-8',   title: 'Platz 5–8',   color: '#fb923c',
    hfRounds: ['Platz 5-8 Halbfinale',   'Platzierung 5-8: Halbfinale',   'Platzierung 5-8 HF'],
    p1: 'Spiel um Platz 5',  p2: 'Spiel um Platz 7'  },
  { key: '9-12',  title: 'Platz 9–12',  color: '#60a5fa',
    hfRounds: ['Platz 9-12 Halbfinale',  'Platzierung 9-12: Halbfinale',  'Platzierung 9-12 HF'],
    p1: 'Spiel um Platz 9',  p2: 'Spiel um Platz 11' },
  { key: '13-16', title: 'Platz 13–16', color: '#a78bfa',
    hfRounds: ['Platz 13-16 Halbfinale', 'Platzierung 13-16: Halbfinale', 'Platzierung 13-16 HF'],
    p1: 'Spiel um Platz 13', p2: 'Spiel um Platz 15' },
];

// ── Hilfsfunktionen ────────────────────────────────────────────────────────────

function computeLayout(rounds, ch, layout) {
  const { CW, GV, GH } = layout;
  const step = ch + GV;
  const pos  = rounds.map(() => []);

  rounds[0].forEach((m, i) => {
    pos[0].push({ match: m, x: 0, y: i * step, cy: i * step + ch / 2 });
  });

  for (let r = 1; r < rounds.length; r++) {
    const prev = pos[r - 1];
    rounds[r].forEach((m, i) => {
      const a  = prev[i * 2];
      const b  = prev[i * 2 + 1];
      const cy = (a && b) ? (a.cy + b.cy) / 2 : a ? a.cy : i * step + ch / 2;
      pos[r].push({ match: m, x: r * (CW + GH), y: cy - ch / 2, cy });
    });
  }
  return pos;
}

function buildLinesSVG(pos, ox, oy, CW, GH) {
  const lines = [];
  for (let r = 0; r < pos.length - 1; r++) {
    for (let ni = 0; ni < pos[r + 1].length; ni++) {
      const target = pos[r + 1][ni];
      const mx     = target.x + ox - GH / 2;
      [pos[r][ni * 2], pos[r][ni * 2 + 1]].filter(Boolean).forEach((src, si) => {
        lines.push({
          key: `${r}-${ni}-${si}`,
          d: `M ${src.x + CW + ox} ${src.cy + oy} H ${mx} V ${target.cy + oy} H ${target.x + ox}`,
        });
      });
    }
  }
  return lines;
}

function sorted(arr) {
  return [...arr].sort((a, b) => (a.match_number || 0) - (b.match_number || 0));
}

// ── MatchCard ──────────────────────────────────────────────────────────────────

function MatchCard({ match, teams, readOnly, compact, isDarkMode, onMatchClick, scheduleMap }) {
  const team1 = teams?.find(t => t.id === match.team1_id);
  const team2 = teams?.find(t => t.id === match.team2_id);

  const isFinished = match.status === 'beendet';
  const isBye      = match.status === 'bye';
  const hasScore   = match.team1_score !== null && match.team2_score !== null;
  const t1wins     = hasScore && match.team1_score > match.team2_score;
  const t2wins     = hasScore && match.team2_score > match.team1_score;

  const label = (team) => {
    if (!team) return 'TBD';
    const players = team.players?.map(p => p.name).join(' / ');
    return players || team.name;
  };

  const bg          = isDarkMode ? '#1e293b' : '#ffffff';
  const borderColor = isFinished
    ? (isDarkMode ? '#166534' : '#86efac')
    : (isDarkMode ? '#334155' : '#e2e8f0');
  const muted      = isDarkMode ? '#64748b' : '#94a3b8';
  const textNormal = isDarkMode ? '#e2e8f0' : '#1e293b';
  const divLine    = isDarkMode ? '#0f172a' : '#f1f5f9';
  const footerBg   = isDarkMode ? '#0f172a' : '#f8fafc';
  const rowBg      = (w) => w ? (isDarkMode ? 'rgba(232,183,58,0.14)' : 'rgba(232,183,58,0.09)') : 'transparent';

  const handleClick = (!readOnly && onMatchClick) ? () => onMatchClick(match) : undefined;

  const fs  = compact ? 11 : (readOnly ? 13.5 : 13);
  const fsS = compact ? 13 : (readOnly ? 16   : 15);
  const pad = compact ? '4px 7px' : (readOnly ? '7px 10px' : '7px 10px');
  const minW = compact ? 18 : (readOnly ? 24 : 24);

  return (
    <div onClick={handleClick} xmlns="http://www.w3.org/1999/xhtml"
      style={{ width: '100%', height: '100%', backgroundColor: bg, border: `1.5px solid ${borderColor}`,
        borderRadius: compact ? 6 : 8, boxShadow: '0 2px 8px rgba(0,0,0,0.13)', overflow: 'hidden',
        cursor: handleClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>

      {isFinished && <div style={{ height: 2, background: `linear-gradient(90deg,${GREEN},${GOLD})`, flexShrink: 0 }} />}

      {/* Court / Zeit */}
      {(() => {
        const sched = scheduleMap?.[match.id];
        const court = match.court || sched?.court;
        const time  = match.start_time || sched?.start_time;
        if (!court && !time) return null;
        return (
          <div style={{
            display: 'flex', gap: compact ? 6 : 8, alignItems: 'center', flexShrink: 0,
            padding: compact ? '2px 7px' : '3px 10px',
            borderBottom: `1px solid ${divLine}`,
            fontSize: compact ? 9 : 10, fontWeight: 600, color: GOLD,
            overflow: 'hidden', whiteSpace: 'nowrap',
          }}>
            {court && <span>🏟 {court}</span>}
            {time  && <span>⏰ {time}</span>}
          </div>
        );
      })()}

      {isBye ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px 8px', color: muted, fontSize: fs }}>
          <span>{label(team1)}</span>
          <span style={{ color: GOLD, fontSize: fs - 1, marginTop: 2 }}>Freilos</span>
        </div>
      ) : (
        <>
          {/* Team 1 */}
          <div style={{ display: 'flex', alignItems: 'center', padding: pad, gap: 5, backgroundColor: rowBg(t1wins), flex: 1, minHeight: 0 }}>
            <span style={{ flex: 1, fontSize: fs, fontWeight: t1wins ? 900 : (compact ? 700 : 600), color: !team1 ? muted : t1wins ? GOLD : textNormal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label(team1)}
            </span>
            <span style={{ fontWeight: 800, fontSize: fsS, minWidth: minW, textAlign: 'center', color: t1wins ? GOLD : muted }}>
              {hasScore ? match.team1_score : '–'}
            </span>
          </div>

          <div style={{ height: 1, backgroundColor: divLine, flexShrink: 0 }} />

          {/* Team 2 */}
          <div style={{ display: 'flex', alignItems: 'center', padding: pad, gap: 5, backgroundColor: rowBg(t2wins), flex: 1, minHeight: 0 }}>
            <span style={{ flex: 1, fontSize: fs, fontWeight: t2wins ? 900 : (compact ? 700 : 600), color: !team2 ? muted : t2wins ? GOLD : textNormal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label(team2)}
            </span>
            <span style={{ fontWeight: 800, fontSize: fsS, minWidth: minW, textAlign: 'center', color: t2wins ? GOLD : muted }}>
              {hasScore ? match.team2_score : '–'}
            </span>
          </div>

          {!readOnly && onMatchClick && (
            <div style={{ flexShrink: 0, borderTop: `1px solid ${divLine}`, padding: '3px 8px', fontSize: 10, textAlign: 'center', backgroundColor: footerBg, color: isFinished ? GREEN : muted }}>
              {isFinished ? '✓ Beendet' : '📝 Klicken zum Eintragen'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── BracketSVG – universelles Bracket aus Rundenarray ─────────────────────────

function BracketSVG({ rounds, roundLabels, place3Match, isDarkMode, teams, readOnly, compact, onMatchClick, accentColor, layout, scheduleMap }) {
  const { CW, GV, GH, OX, OY } = layout;
  const ch = readOnly ? layout.CH_RO : layout.CH_ADMIN;

  const positions = computeLayout(rounds, ch, layout);
  const lines     = buildLinesSVG(positions, OX, OY, CW, GH);
  const lineColor = isDarkMode ? '#374151' : '#cbd5e1';
  const allPos    = positions.flat();
  const numCols   = rounds.length;

  const maxY      = allPos.length ? Math.max(...allPos.map(p => p.y + ch)) : ch;
  const svgW      = numCols * (CW + GH) - GH + OX * 2;
  const finalColX = (numCols - 1) * (CW + GH) + OX;
  const p3Y       = maxY + GV * 2.5 + OY;
  const svgH      = place3Match ? p3Y + ch + 20 : maxY + OY + 20;

  const color      = accentColor || GOLD;
  const labelFs    = compact ? 12 : (readOnly ? 16 : 13);
  const labelColor = readOnly || compact ? (isDarkMode ? '#f1f5f9' : '#1e293b') : color;
  const labelY     = compact ? 17 : (readOnly ? 20 : 16);

  return (
    <svg width={svgW} height={svgH} style={{ display: 'block', overflow: 'visible' }}>
      {roundLabels.map((lbl, r) => (
        <text key={lbl + r} x={r * (CW + GH) + CW / 2 + OX} y={labelY}
          textAnchor="middle" fill={labelColor} fontSize={labelFs}
          fontWeight="800" style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {lbl}
        </text>
      ))}

      {lines.map(({ key, d }) => (
        <path key={key} d={d} stroke={lineColor} strokeWidth={compact ? 1.5 : 2} fill="none" strokeLinecap="round" />
      ))}

      {allPos.map(({ match, x, y }) => (
        <foreignObject key={match.id} x={x + OX} y={y + OY} width={CW} height={ch}>
          <MatchCard match={match} teams={teams} readOnly={readOnly} compact={compact} isDarkMode={isDarkMode} onMatchClick={onMatchClick} scheduleMap={scheduleMap} />
        </foreignObject>
      ))}

      {place3Match && (
        <>
          <text x={finalColX + CW / 2} y={p3Y - (compact ? 6 : 8)} textAnchor="middle"
            fill={readOnly || compact ? labelColor : (isDarkMode ? '#94a3b8' : '#64748b')}
            fontSize={compact ? 11 : (readOnly ? 14 : 10)}
            fontWeight="700" style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Spiel um Platz 3
          </text>
          <line x1={finalColX} y1={maxY + OY + GV} x2={finalColX + CW} y2={maxY + OY + GV}
            stroke={lineColor} strokeWidth={1.5} strokeDasharray="4 4" />
          <foreignObject x={finalColX} y={p3Y} width={CW} height={ch}>
            <MatchCard match={place3Match} teams={teams} readOnly={readOnly} compact={compact} isDarkMode={isDarkMode} onMatchClick={onMatchClick} scheduleMap={scheduleMap} />
          </foreignObject>
        </>
      )}
    </svg>
  );
}

// ── PlacementMini – Platz 5-8 / 9-12 / 13-16 ─────────────────────────────────
// Struktur:  [HF0]           [Winner-Final / p1]    z.B. "Spiel um Platz 5"
//                 ↘ (convergence)
//            [HF1]
//
//            --- Trennlinie ---
//
//            [Loser-Final / p2]                     z.B. "Spiel um Platz 7" (standalone)

function PlacementMini({ config, byRound, teams, isDarkMode, readOnly, compact, onMatchClick, layout, scheduleMap }) {
  const { CW, GH, OX, OY } = layout;
  const ch = readOnly ? layout.CH_RO : layout.CH_ADMIN;

  const hfMatches = sorted(
    (config.hfRounds || [config.hfRound]).flatMap(r => byRound[r] || [])
  );
  const p1Match = (byRound[config.p1] || [])[0];
  const p2Match = (byRound[config.p2] || [])[0];

  if (!hfMatches.length && !p1Match && !p2Match) return null;

  const { color } = config;
  const lineColor  = isDarkMode ? '#374151' : '#cbd5e1';
  const borderMuted = isDarkMode ? '#1e293b' : '#e2e8f0';
  const labelFs    = compact ? 9 : 10;
  const labelY     = compact ? 14 : 16;

  // ── Winner-Bracket: HF[] → p1 ─────────────────────────────────────────────
  const winnerRounds = [];
  if (hfMatches.length > 0) winnerRounds.push(hfMatches);
  if (p1Match)              winnerRounds.push([p1Match]);

  const winnerLabels = winnerRounds.map((_, i) => {
    if (i === 0) return 'Halbfinale';
    return config.p1.replace('Spiel um ', '');
  });

  const positions  = winnerRounds.length > 0 ? computeLayout(winnerRounds, ch, layout) : [];
  const lines      = positions.length > 1 ? buildLinesSVG(positions, OX, OY, CW, GH) : [];
  const allPos     = positions.flat();
  const numCols    = winnerRounds.length;
  const maxY       = allPos.length ? Math.max(...allPos.map(p => p.y + ch)) : ch;
  const svgW       = numCols > 0 ? numCols * (CW + GH) - GH + OX * 2 : CW + OX * 2;
  const svgH       = maxY + OY + 16;

  // ── Loser-Final: p2 standalone ────────────────────────────────────────────
  const p2SvgW = CW + OX * 2;
  const p2SvgH = ch + OY + 16;

  return (
    <div style={{ flex: '1 1 0', minWidth: compact ? 200 : 260 }}>
      {/* Sektion-Titel */}
      <h4 style={{ color, textTransform: 'uppercase', letterSpacing: '0.07em',
        fontSize: compact ? 10 : 11, fontWeight: 700, textAlign: 'center',
        marginBottom: compact ? 6 : 8, marginTop: 0 }}>
        {config.title}
      </h4>

      {/* Winner-Bracket */}
      {winnerRounds.length > 0 && (
        <svg width={svgW} height={svgH} style={{ display: 'block', overflow: 'visible' }}>
          {/* Spaltenbezeichnungen */}
          {winnerLabels.map((lbl, r) => (
            <text key={lbl + r} x={r * (CW + GH) + CW / 2 + OX} y={labelY}
              textAnchor="middle" fill={color} fontSize={labelFs}
              fontWeight="700" style={{ letterSpacing: '0.06em' }}>
              {lbl}
            </text>
          ))}

          {/* Verbindungslinien (sauber konvergierend) */}
          {lines.map(({ key, d }) => (
            <path key={key} d={d} stroke={lineColor}
              strokeWidth={compact ? 1.5 : 2} fill="none" strokeLinecap="round" />
          ))}

          {/* Match-Karten */}
          {allPos.map(({ match, x, y }) => (
            <foreignObject key={match.id} x={x + OX} y={y + OY} width={CW} height={ch}>
              <MatchCard match={match} teams={teams} readOnly={readOnly} compact={compact}
                isDarkMode={isDarkMode} onMatchClick={onMatchClick} scheduleMap={scheduleMap} />
            </foreignObject>
          ))}
        </svg>
      )}

      {/* Trennlinie + Loser-Final */}
      {p2Match && (
        <div style={{ marginTop: compact ? 10 : 14 }}>
          {/* Trennlinie mit Label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: compact ? 6 : 8 }}>
            <div style={{ flex: 1, height: 1, backgroundColor: borderMuted }} />
            <span style={{ fontSize: compact ? 8 : 9, fontWeight: 700,
              color: isDarkMode ? '#475569' : '#94a3b8',
              textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>
              {config.p2.replace('Spiel um ', '')}
            </span>
            <div style={{ flex: 1, height: 1, backgroundColor: borderMuted }} />
          </div>

          {/* Loser-Final Karte */}
          <svg width={p2SvgW} height={p2SvgH} style={{ display: 'block', overflow: 'visible' }}>
            <foreignObject x={OX} y={OY} width={CW} height={ch}>
              <MatchCard match={p2Match} teams={teams} readOnly={readOnly} compact={compact}
                isDarkMode={isDarkMode} onMatchClick={onMatchClick} scheduleMap={scheduleMap} />
            </foreignObject>
          </svg>
        </div>
      )}
    </div>
  );
}

// ── Haupt-Komponente ───────────────────────────────────────────────────────────

export default function TournamentBracket({ matches, teams, readOnly, compact, isDarkMode, onMatchClick, scheduleMap }) {
  const layout = compact ? COMPACT_LAYOUT : NORMAL_LAYOUT;

  if (!matches?.length) {
    return (
      <div style={{ textAlign: 'center', color: isDarkMode ? '#64748b' : '#94a3b8', padding: '40px 0', fontSize: 14 }}>
        Keine K.O.-Matches vorhanden
      </div>
    );
  }

  const byRound = {};
  matches.forEach(m => {
    if (!byRound[m.round]) byRound[m.round] = [];
    byRound[m.round].push(m);
  });

  const mainRoundNames = MAIN_ORDER.filter(r => byRound[r]?.length > 0);
  const mainRounds     = mainRoundNames.map(r => sorted(byRound[r]));
  const place3Match    = byRound[PLACE3]?.[0] || null;

  const activePlacements = PLACEMENT_CONFIGS.filter(
    c => (c.hfRounds || [c.hfRound]).some(r => byRound[r]?.length) ||
         byRound[c.p1]?.length || byRound[c.p2]?.length
  );

  const sectionBg   = isDarkMode ? 'rgba(30,41,59,0.4)' : 'rgba(248,250,252,0.8)';
  const borderMuted = isDarkMode ? '#1e293b' : '#e2e8f0';

  return (
    <div style={readOnly || compact ? { width: 'fit-content', margin: '0 auto' } : { width: '100%' }}>
      {/* ── Hauptbaum ── */}
      {mainRounds.length > 0 && (
        <div style={{ marginBottom: activePlacements.length ? (compact ? 20 : 32) : 0 }}>
          {!readOnly && !compact && (
            <h3 style={{ color: GOLD, fontSize: 15, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.1em', textAlign: 'center', margin: '0 0 16px 0' }}>
              🏆 Turnierbaum
            </h3>
          )}
          <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
            <BracketSVG
              rounds={mainRounds}
              roundLabels={mainRoundNames}
              place3Match={place3Match}
              isDarkMode={isDarkMode}
              teams={teams}
              readOnly={readOnly}
              compact={compact}
              onMatchClick={onMatchClick}
              layout={layout}
              scheduleMap={scheduleMap}
            />
          </div>
        </div>
      )}

      {/* ── Platzierungsbrackets ── */}
      {activePlacements.length > 0 && (
        <div>
          {/* Trennlinie mit Titel */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12,
            margin: `0 0 ${compact ? 14 : 20}px` }}>
            <div style={{ flex: 1, height: 1, backgroundColor: borderMuted }} />
            <span style={{ fontSize: compact ? 10 : 12, fontWeight: 700,
              color: isDarkMode ? '#475569' : '#94a3b8',
              textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
              Platzierungsspiele
            </span>
            <div style={{ flex: 1, height: 1, backgroundColor: borderMuted }} />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 16 : 24, justifyContent: 'center' }}>
            {activePlacements.map(config => (
              <div key={config.key} style={{
                backgroundColor: sectionBg,
                border: `1px solid ${config.color}28`,
                borderRadius: compact ? 8 : 10,
                padding: compact ? '10px 12px' : '14px 16px',
              }}>
                <PlacementMini
                  config={config}
                  byRound={byRound}
                  teams={teams}
                  isDarkMode={isDarkMode}
                  readOnly={readOnly}
                  compact={compact}
                  onMatchClick={onMatchClick}
                  layout={layout}
                  scheduleMap={scheduleMap}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
